/**
 * KisiKunyeSeridi.jsx — Kart Yazdır ve Kimlik Doğrula / Foto Ekle pencerelerinin ortak "künye" şeridi
 * (backlog #350, #352; backend #351 fotoğraf).
 *
 * İki pencere aynı kişiyi gösterir; üstlerinde AYNI şerit durur: solda vesikalık, ortada ad soyad / T.C. / telefon /
 * kimlik doğrulama durumu / firma, sağda fuar logosu. Veri çekmez (vesikalık hariç: `useKisiFoto`), her şey prop.
 *
 * Kimlik durumu bu temada `success` gri, `secondary` yeşil olduğu için StandPills'in sabit hex paletinden boyanır
 * (0 gri, 1 yeşil, 2 kırmızı). `onKimlik` verilirse küçük bir düğme çıkar: durum 1 değilken "Kimlik Doğrula / Foto Ekle",
 * doğrulanmışken "Doğrulamayı güncelle" (#470; WAS: doğrulanmışken düğme hiç çıkmazdı)
 * (Kart Yazdır: Kimlik penceresini üstüne açar; Kimlik penceresinin kendisi vermez).
 * `fotoYukle(file)` verilirse vesikalığın köşesinde kamera düğmesi çıkar; tür denetimi çağıranın işi
 * (KunyeBasligi ile aynı JPG / PNG / WEBP kuralı).
 *
 * @param {object}   person            { personId, name, surname, tcNo, tcNoMasked, phone, kimlikDogrulama?, fotografVar? }
 * @param {number}   kimlikDurum       0/1/2; verilmezse `person.kimlikDogrulama.durum` (yoksa 0). `null` → durum hiç gösterilmez
 *                                     (karar henüz okunuyor, yanlış "doğrulanmadı" yazılmasın)
 * @param {boolean}  fotografVar       verilmezse `person.fotografVar`; false iken vesikalık HİÇ istenmez
 * @param {number}   fotoSurum         değişince vesikalık yeniden okunur (yeni yükleme)
 * @param {object}   fotografYukleme   { yukleyenAdi, firmaAdi, firmaYukledi, tarih } — kimin / ne zaman yüklediği
 * @param {string}   firmaAdi          kişinin bu bağlamdaki firması; yoksa satır çıkmaz
 * @param {number}   firmaEki          diğer firma sayısı ("+N")
 * @param {string}   fuarAdi           sağdaki logonun alt yazısı; logo yoksa adın kendisi
 * @param {string}   fuarLogoUrl       fuarın logosu (`fuarLogoUrl`)
 * @param {Function} onKimlik          () => void
 * @param {Function} fotoYukle         (file) => void
 * @param {boolean}  fotoYukleniyor    kamera düğmesinde döner
 */
import React, { useRef } from "react";
import { Button, Spinner } from "reactstrap";
import { Building2, Camera, IdCard, Phone, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import FuarLogoImg from "../Fairs/FuarLogoImg";
import { STATUS_HEX, NEUTRAL_GRAY } from "../Projects/StandPills";
import useKisiFoto from "./useKisiFoto";
import { currentLocaleTag } from "../../i18n";

/** Kimlik durumu → kare rengi (bu temada `success` gri, bu yüzden bootstrap sınıfı değil hex). */
export const KIMLIK_HEX = { 0: NEUTRAL_GRAY, 1: STATUS_HEX.success, 2: STATUS_HEX.danger, 3: STATUS_HEX.info }; // #664: 3 = belge masada onaylı
const KIMLIK_ANAHTAR = { 0: "kimlikDogrulanmadi", 1: "kimlikDogrulandi", 2: "kimlikReddedildi", 3: "kimlikAlandaDogrulanacak" };

/** "2026-10-13 14:30:00" (backend) → yerel kısa tarih-saat; çözülemezse olduğu gibi. */
function tarihYaz(deger) {
  if (!deger) return null;
  const d = new Date(String(deger).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(deger) : d.toLocaleString(currentLocaleTag(), { dateStyle: "short", timeStyle: "short" });
}

export default function KisiKunyeSeridi({
  person, kimlikDurum, fotografVar, fotoSurum = 0, fotografYukleme = null, firmaAdi = null, firmaEki = 0,
  fuarAdi = null, fuarLogoUrl = null, onKimlik, fotoYukle, fotoYukleniyor = false,
}) {
  const { t } = useTranslation("components");
  const girdiRef = useRef(null);
  const fotoUrl = useKisiFoto(person?.personId, fotografVar ?? Boolean(person?.fotografVar), fotoSurum);

  const ham = kimlikDurum === undefined ? (person?.kimlikDogrulama?.durum ?? 0) : kimlikDurum;
  const durum = ham == null ? null : (KIMLIK_ANAHTAR[Number(ham)] ? Number(ham) : 0);
  const durumYazisi = durum == null ? null : t(`kisiKunyeSeridi.${KIMLIK_ANAHTAR[durum]}`);

  const adSoyad = [person?.name, person?.surname].filter(Boolean).join(" ");
  const kimlikNo = person?.tcNo || person?.tcNoMasked || null;
  const yukleyen = fotografYukleme
    ? [fotografYukleme.yukleyenAdi, fotografYukleme.firmaAdi && `(${fotografYukleme.firmaAdi})`].filter(Boolean).join(" ")
    : "";
  const yuklemeTarihi = tarihYaz(fotografYukleme?.tarih);

  function dosyaSecildi(e) {
    const dosya = e.target.files?.[0];
    e.target.value = ""; // aynı dosya tekrar seçilebilsin
    if (dosya) fotoYukle?.(dosya);
  }

  return (
    <div className="d-flex align-items-center gap-3 border rounded-3 p-2 mb-3">
      <div className="position-relative flex-shrink-0" style={{ width: 80, height: 80 }}>
        <div className="rounded-3 border d-flex align-items-center justify-content-center bg-light overflow-hidden w-100 h-100">
          {fotoUrl
            ? <img src={fotoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <User size={36} className="text-muted" />}
        </div>
        {fotoYukle && (
          <>
            <input ref={girdiRef} type="file" accept="image/jpeg,image/png,image/webp" className="d-none" onChange={dosyaSecildi} />
            <Button
              color="light" size="sm" disabled={fotoYukleniyor}
              title={t("kisiKunyeSeridi.fotografiDegistir")} aria-label={t("kisiKunyeSeridi.fotografiDegistir")}
              className="position-absolute rounded-circle border shadow-sm p-0 d-flex align-items-center justify-content-center"
              style={{ width: 26, height: 26, right: -6, bottom: -6 }}
              onClick={() => girdiRef.current?.click()}
            >
              {fotoYukleniyor ? <Spinner size="sm" style={{ width: 12, height: 12 }} /> : <Camera size={13} />}
            </Button>
          </>
        )}
      </div>

      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="fw-bold text-break lh-sm">{adSoyad || "—"}</div>
        <div className="d-flex align-items-center flex-wrap gap-3 small">
          <span className="font-monospace">{kimlikNo || "—"}</span>
          {person?.phone && (
            <span className="d-inline-flex align-items-center gap-1 text-muted"><Phone size={12} />{person.phone}</span>
          )}
        </div>
        {durum != null && (
          <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
            <span
              className="d-inline-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
              style={{ width: 24, height: 24, background: KIMLIK_HEX[durum] }} title={durumYazisi}
            >
              <IdCard size={14} color="#fff" />
            </span>
            <span className="small">{durumYazisi}</span>
            {onKimlik && (
              <Button color="primary" outline size="sm" className="py-0 px-2" onClick={onKimlik}>
                {durum === 1 ? t("kisiKunyeSeridi.dogrulamayiGuncelle") : t("kisiKunyeSeridi.kimlikDogrulaFotoEkle")}
              </Button>
            )}
          </div>
        )}
        {firmaAdi && (
          <div className="d-flex align-items-center gap-1 mt-1 small text-break">
            <Building2 size={13} className="text-muted flex-shrink-0" />
            <span>{firmaAdi}</span>
            {firmaEki > 0 && <span className="text-muted">+{firmaEki}</span>}
          </div>
        )}
        {(yukleyen || yuklemeTarihi) && (
          <div className="text-muted small mt-1">
            {t("kisiKunyeSeridi.yukleyen")}: {[yukleyen, yuklemeTarihi].filter(Boolean).join(" · ")}
          </div>
        )}
      </div>

      {(fuarLogoUrl || fuarAdi) && (
        <div className="flex-shrink-0 ms-auto text-end" style={{ maxWidth: 140 }}>
          {fuarLogoUrl
            ? <FuarLogoImg url={fuarLogoUrl} alt={fuarAdi || ""} title={fuarAdi || undefined} style={{ maxHeight: 44, maxWidth: 140, objectFit: "contain" }} />
            : <span className="text-muted small">{fuarAdi}</span>}
        </div>
      )}
    </div>
  );
}
